import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';

const API_BASE_URL = (
  process.env.REACT_APP_BACKEND_URL || 'https://api.nubiasolar.es'
).replace(/\/$/, '');

const Contacto = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    message: '',
    acceptPrivacy: false,
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      location: '',
      message: '',
      acceptPrivacy: false,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.acceptPrivacy) {
      toast({
        title: 'Error',
        description: 'Debes aceptar la política de privacidad.',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          location: formData.location.trim(),
          message: formData.message.trim(),
          source: 'web-contact-form',
        }),
      });

      if (!response.ok) {
        throw new Error('No se pudo registrar la solicitud.');
      }

      toast({
        title: '¡Solicitud enviada!',
        description: 'Hemos recibido tu solicitud. Te responderemos lo antes posible.',
      });
      resetForm();
    } catch (error) {
      toast({
        title: 'No se pudo enviar la solicitud',
        description: 'Ha habido un problema al registrar tu mensaje. Prueba de nuevo o llámanos directamente al 604 92 17 45.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Contacto - Solicita tu Presupuesto de Placas Solares | Nubia Solar</title>
        <meta
          name="description"
          content="Contacta con Nubia Solar para instalar placas solares en Madrid, Valencia o Extremadura. Presupuesto gratuito sin compromiso. ☎️ 604 92 17 45"
        />
      </Helmet>

      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-nubia-green-primary mb-6">Contacto</h1>
            <p className="text-xl text-nubia-text-dark/70 leading-relaxed">
              Estamos aquí para ayudarte en tu transición hacia la energía solar.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <Card className="nubia-shadow border-2 border-nubia-gray-light">
                  <CardHeader>
                    <CardTitle className="text-3xl text-nubia-green-primary">Solicita tu presupuesto</CardTitle>
                    <CardDescription className="text-lg">
                      Completa el formulario y registraremos tu solicitud correctamente.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <Label htmlFor="name" className="text-nubia-green-primary font-semibold">Nombre completo *</Label>
                        <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Tu nombre" required className="mt-2 border-2 focus:border-nubia-gold-primary" />
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="email" className="text-nubia-green-primary font-semibold">Email *</Label>
                          <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="tu@email.com" required className="mt-2 border-2 focus:border-nubia-gold-primary" />
                        </div>

                        <div>
                          <Label htmlFor="phone" className="text-nubia-green-primary font-semibold">Teléfono *</Label>
                          <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="+34 600 000 000" required className="mt-2 border-2 focus:border-nubia-gold-primary" />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="location" className="text-nubia-green-primary font-semibold">Ubicación</Label>
                        <Input id="location" name="location" value={formData.location} onChange={handleChange} placeholder="Ciudad o código postal" className="mt-2 border-2 focus:border-nubia-gold-primary" />
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-nubia-green-primary font-semibold">Mensaje</Label>
                        <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Cuéntanos sobre tu proyecto solar..." rows={5} className="mt-2 border-2 focus:border-nubia-gold-primary" />
                      </div>

                      <div className="flex items-start gap-3">
                        <Checkbox
                          id="privacy"
                          checked={formData.acceptPrivacy}
                          onCheckedChange={(checked) =>
                            setFormData((prev) => ({ ...prev, acceptPrivacy: Boolean(checked) }))
                          }
                        />
                        <Label htmlFor="privacy" className="text-sm leading-relaxed cursor-pointer text-nubia-text-dark/70">
                          Acepto la política de privacidad y el tratamiento de mis datos personales *
                        </Label>
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        className="w-full bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold py-6 text-lg shadow-lg disabled:opacity-70"
                      >
                        <Send className="mr-2 w-5 h-5" />
                        {isSubmitting ? 'Enviando solicitud...' : 'Enviar solicitud'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card className="nubia-shadow border-2 border-nubia-gray-light bg-gradient-to-br from-nubia-green-primary to-nubia-green-secondary text-white">
                  <CardHeader>
                    <CardTitle className="text-2xl text-nubia-gold-primary">Información de contacto</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-nubia-gold-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-nubia-gold-primary" />
                      </div>
                      <div>
                        <div className="font-semibold mb-1 text-nubia-gold-soft">Teléfono</div>
                        <a href="tel:+34604921745" className="text-white hover:text-nubia-gold-soft transition-colors text-lg">+34 604 92 17 45</a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-nubia-gold-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-nubia-gold-primary" />
                      </div>
                      <div>
                        <div className="font-semibold mb-1 text-nubia-gold-soft">Email</div>
                        <a href="mailto:info@nubiasolar.com" className="text-white hover:text-nubia-gold-soft transition-colors">info@nubiasolar.com</a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-nubia-gold-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-nubia-gold-primary" />
                      </div>
                      <div>
                        <div className="font-semibold mb-1 text-nubia-gold-soft">Oficina central</div>
                        <p className="text-white">Madrid, España</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="nubia-shadow border-2 border-nubia-gray-light">
                  <CardHeader>
                    <div className="w-12 h-12 bg-nubia-gold-soft/20 rounded-xl flex items-center justify-center mb-2">
                      <Clock className="w-6 h-6 text-nubia-gold-primary" />
                    </div>
                    <CardTitle className="text-xl text-nubia-green-primary">Horario de atención</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-nubia-text-dark/70">Lunes - Viernes:</span>
                      <span className="font-semibold text-nubia-green-primary">9:00 - 18:00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-nubia-text-dark/70">Sábados:</span>
                      <span className="font-semibold text-nubia-green-primary">10:00 - 14:00</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-nubia-text-dark/70">Domingos:</span>
                      <span className="font-semibold text-nubia-text-dark/40">Cerrado</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="nubia-shadow border-2 border-nubia-gold-primary bg-nubia-gold-soft/10">
                  <CardHeader>
                    <CardTitle className="text-xl text-nubia-green-primary">¿Prefieres llamar?</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="mb-4 text-nubia-text-dark/70">Habla directamente con nuestros expertos y resuelve tus dudas al instante.</p>
                    <Button size="lg" className="w-full bg-nubia-green-primary hover:bg-nubia-green-secondary text-white" onClick={() => window.location.href = 'tel:+34604921745'}>
                      <Phone className="mr-2 w-5 h-5" />
                      Llamar ahora
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-nubia-gray-light">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <Card className="nubia-shadow border-2 border-nubia-gray-light overflow-hidden">
              <div className="h-[400px] w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d194348.38088265457!2d-3.8196207250000003!3d40.4378698!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd422997800a3c81%3A0xc436dec1618c2269!2sMadrid%2C%20Espa%C3%B1a!5e0!3m2!1ses!2ses!4v1234567890123!5m2!1ses!2ses"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mapa de Madrid, España"
                ></iframe>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contacto;
